<template>
  <div>
    <div>
      <b-table
        :data="actions"
        :striped="true"
        :narrowed="true"
        :hoverable="true"
        :sort-icon="'angle-up'"
        @click="rowClicked"
        :selected.sync="rowSelected"
        paginated
        per-page="10"
        ref="actions-table"
        :rowClass="rowClass"
      >
        <b-table-column
          :visible="withActionId"
          field="id"
          :label="$t('compTransitionActionsTable.column-id')"
          centered
          v-slot="props"
          sortable
          :searchable="searchable"
        >
          {{ props.row.id }}
        </b-table-column>

        <b-table-column
          :visible="withLabColumn"
          field="entity.name"
          :label="$t('compTransitionActionsTable.column-lab')"
          centered
          v-slot="props"
          sortable
          :searchable="searchable"
        >
          {{ props.row.entity.name }}
        </b-table-column>

        <b-table-column
          :visible="withValidColumn"
          field="adminStatus"
          :label="$t('compTransitionActionsTable.column-valid')"
          centered
          v-slot="props"
          sortable
        >
          <action-tag :tag="props.row.adminStatus" />
        </b-table-column>

        <b-table-column
          field="title"
          :label="$t('compTransitionActionsTable.column-title')"
          centered
          :searchable="searchable"
          :placeholder="$t('compTransitionActionsTable.search-names')"
          size="is-small"
          sortable
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('compTransitionActionsTable.search-names')"
              icon="magnify"
              size="is-small"
            />
          </template>
          <template v-slot="props">
            {{ props.row.title }}
          </template>
        </b-table-column>

        <b-table-column
          field="createdAt"
          :label="$t('compTransitionActionsTable.column-createdAt')"
          :visible="withCreatedAtColumn"
          centered
          v-slot="props"
          sortable
        >
          {{ $d(props.row.createdAt, 'short') }}
        </b-table-column>

        <b-table-column
          field="updatedAt"
          :label="$t('compTransitionActionsTable.column-updatedAt')"
          :visible="withUpdatedAtColumn"
          centered
          v-slot="props"
          sortable
        >
          {{ $d(props.row.updatedAt, 'short') }}
        </b-table-column>

        <b-table-column
          field="status"
          :label="$t('compTransitionActionsTable.column-status')"
          :visible="withStatusColumn"
          centered
          v-slot="props"
          sortable
        >
          <action-tag :tag="props.row.status"></action-tag>
        </b-table-column>

        <!-- tags -->
        <b-table-column
          field="tags"
          :label="$t('compTransitionActionsTable.column-tags')"
          :searchable="searchable"
          :custom-search="searchTags"
          width="20%"
        >
          <template #searchable="props">
            <b-taginput
              :allow-duplicate="false"
              ellipsis
              :data="filteredTags"
              autocomplete
              @typing="getFilteredTags"
              :allow-new="false"
              :open-on-focus="true"
              icon="label"
              aria-close-label="Delete this tag"
              ref="taginput"
              v-model="props.filters[props.column.field]"
              :placeholder="$t('compTransitionActionsTable.search-tags')"
              size="is-small"
            >
              <!-- suggested items -->
              <template slot-scope="props">
                <action-tag
                  :tag="props.option"
                  :tooltipPosition="'is-right'"
                  :tooltipMultilined="false"
                ></action-tag>
              </template>
              <!-- selected items -->
              <template #selected="props">
                <action-tag
                  v-for="(tag, index) in props.tags"
                  :key="index"
                  :tag="tag"
                  closable
                  @close="$refs.taginput.removeTag(index, $event)"
                >
                </action-tag>
              </template>
            </b-taginput>
          </template>

          <!-- Actions tags -->
          <template v-slot="props">
            <action-tag
              v-for="item in props.row.tags"
              :key="item.descriptor"
              :tag="item"
            ></action-tag>
          </template>
        </b-table-column>

        <b-table-column
          field="reminder"
          :label="$t('compTransitionActionsTable.column-reminder')"
          :visible="withReminderColumn"
          centered
          v-slot="props"
          sortable
        >
          {{ props.row.reminder ? $d(props.row.reminder, 'short') : '' }}
        </b-table-column>
        <b-table-column
          :visible="withReviewerColumn"
          field="reviewer"
          :label="$t('compTransitionActionsTable.column-reviewer')"
          centered
          sortable
          :searchable="searchable"
        >
          <template #searchable="props">
            <b-input
              v-model="props.filters[props.column.field]"
              :placeholder="$t('compTransitionActionsTable.search-reviewer')"
              icon="magnify"
              size="is-small"
            />
          </template>
          <template v-slot="props">
            {{ props.row.reviewer }}
          </template>
        </b-table-column>

        <b-table-column v-slot="props">
          <div>
            <slot name="custom" :action="props.row"></slot>
          </div>
        </b-table-column>

        <section slot="empty" class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <i class="fa fa-ban" style="font-size: 64px"></i>
            </p>
            <slot name="empty-actions">
              <p>{{ $t('compTransitionActionsTable.no-actions') }}</p>
            </slot>
          </div>
        </section>
      </b-table>
    </div>
  </div>
</template>

<script>
import transitionService from '@/services/transitionService.js'
import { Background } from '@/mixins/background.js'

import _ from 'lodash'

import { TAGS } from '@/models/transition/transition.js'

export default {
  name: 'ActionsTable',
  props: {
    actions: {
      type: Array,
      required: true
    },
    searchable: {
      type: Boolean,
      required: false,
      default: true
    },
    withValidColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    withCreatedAtColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    withUpdatedAtColumn: {
      type: Boolean,
      required: false,
      default: true
    },
    withActionId: {
      type: Boolean,
      required: false,
      default: false
    },
    withReviewerColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    withReminderColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    withLabColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    withStatusColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    rowClass: {
      type: Function,
      required: false,
      default: () => ''
    }
  },
  components: {
    'action-tag': () => import('@/components/transition/ActionTag.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  mixins: [Background],
  emit: ['is-valid', 'is-modified', 'onValidAction', 'onViewAction'],
  data: function () {
    return {
      // Erreur catch somehow and displayed by the serveur-info component
      erreur: '',
      // The list of possible tags
      // It's used to make suggestions on the corresponding tag inputs
      availableTags: TAGS,
      filteredTags: TAGS,
      rowSelected: null,
      testBind: null
    }
  },
  watch: {},
  methods: {
    getFilteredTags (text) {
      // support autocompletion for several lang
      this.filteredTags = this.availableTags.filter((tag) => tag.match(text))
    },
    rowClicked (selected) {
      this.$emit('row-clicked', selected)
    },
    /**
     *
     * Search function triggered when adding a tag on the search filter
     * We filter based on the descriptor tag which is locale agnostic
     * @param {Plan} plan
     * @param {Tag[]} input
     */
    searchTags (action, input) {
      let planDescriptors = action.tags.map((t) => t.descriptor)
      let inputDescriptors = input.map((t) => t.descriptor)
      let intersection = _.intersection(planDescriptors, inputDescriptors)
      return intersection.length === inputDescriptors.length
    },
    clickFile (file) {
      transitionService
        .getFile(file)
        .then((data) => window.open(URL.createObjectURL(data)))
        .catch(() => {
          let route = this.$router.resolve({
            path: '/not-found'
          })
          window.open(route.href, '_blank')
        })
    }
  }
}
</script>
